import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-apollo';
import pMap from 'p-map-browser';

import { rosterIdentityLinkedIdentityCreate as rosterIdentityLinkedIdentityCreateMutation } from '../../../graphql/mutations/roster-identity';
import { resetRosterIdentities } from '../../../actions/discover-album-folder';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { gtm } from '../../../util/analytics';
import { getErrorString } from '../../../util/normalize-errors';
import sleep from '../../../util/sleep';
import { NO_FR_JOIN_TRACKING } from '../../../constants/discover-album-folder';

// Time given to the backend to link the identities to the event before we
// land on the matches page, otherwise it renders empty.
const JOIN_SETTLE_MS = 5000;

/**
 * Joins the selected roster identities to a face-recognition-disabled event
 * and redirects to the matches page.
 *
 * @param {Object} options
 * @param {string} options.albumFolderId
 * @returns {{ onJoin: Function, isJoining: boolean }}
 */
const useNoFrJoinSubmit = ({ albumFolderId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const openAlert = useOpenAlert();
  const [isJoining, setIsJoining] = useState(false);

  const [rosterIdentityLinkedIdentityCreate] = useMutation(
    rosterIdentityLinkedIdentityCreateMutation,
  );

  const onJoin = async (rosterIdentities = []) => {
    if (isJoining || rosterIdentities.length === 0) return;

    setIsJoining(true);
    gtm.trackEvent({
      type: NO_FR_JOIN_TRACKING.TYPE,
      action: NO_FR_JOIN_TRACKING.ACTION.JOIN_SUBMITTED,
      label: albumFolderId,
      identity_count: rosterIdentities.length,
    });

    try {
      await pMap(rosterIdentities, async rosterIdentity =>
        rosterIdentityLinkedIdentityCreate({
          variables: {
            input: {
              roster_identity_id: rosterIdentity.id,
              hierarchy_item_id: albumFolderId,
            },
          },
        }),
      );

      gtm.trackEvent({
        type: NO_FR_JOIN_TRACKING.TYPE,
        action: NO_FR_JOIN_TRACKING.ACTION.JOIN_SUCCEEDED,
        label: albumFolderId,
        identity_count: rosterIdentities.length,
      });

      await sleep(JOIN_SETTLE_MS);
      // Clear the selection so it cannot leak into another event's onboarding.
      dispatch(resetRosterIdentities());
      // The redirect unmounts the caller, so isJoining is only reset on failure.
      history.push(`/consumer/matches/${albumFolderId}`);
    } catch (e) {
      setIsJoining(false);
      gtm.trackEvent({
        type: NO_FR_JOIN_TRACKING.TYPE,
        action: NO_FR_JOIN_TRACKING.ACTION.JOIN_FAILED,
        label: albumFolderId,
      });
      openAlert({
        message: 'There was an error joining the event.',
        info: getErrorString(e),
      });
    }
  };

  return { onJoin, isJoining };
};

export default useNoFrJoinSubmit;
